import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { otpVerificationSchema } from '../schemas/otpVerificationSchema';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendOtp, verifyOtp } from '../api/auth';
import LabeledTextField from '../components/LabeledTextField';
import UserPageLayout from '../components/UserPageLayout';
import { Box, Button, Alert, Stack } from '@mui/material';

const OtpVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const phoneFromState = location.state?.phone || '';

  const [phone] = useState(phoneFromState);
  const [message, setMessage] = useState('');
  const [resendAvailable, setResendAvailable] = useState(false);
  const [timer, setTimer] = useState(60);
  const timerRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
  } = useForm({
    resolver: zodResolver(otpVerificationSchema),
    defaultValues: {
      otp: '',
    },
  });

  useEffect(() => {
    if (!phoneFromState) {
      navigate('/forgot-password');
    } else {
      startTimer();
    }
    return () => clearInterval(timerRef.current);
  }, [phoneFromState, navigate]);

  const startTimer = () => {
    setResendAvailable(false);
    setTimer(60);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setResendAvailable(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onSubmit = async (data) => {
    setFormError('root', { message: '' });
    setMessage('');

    try {
      const res = await verifyOtp(phone, data.otp);

      if (res.success) {
        setMessage('認証に成功しました。パスワード再設定画面へ移動します。');
        localStorage.setItem('resetPhone', phone);
        setTimeout(() => {
          navigate('/reset-password-phone', {
            state: { verifyToken: res.verify_token },
          });
        }, 1500);
      } else {
        setFormError('root', {
          type: 'manual',
          message: res.message || '認証コードが違います',
        });
      }
    } catch (error) {
      setFormError('root', {
        type: 'manual',
        message: error.response?.data?.message || '通信エラーが発生しました',
      });
    }
  };

  const resendOtp = async () => {
    setFormError('root', { message: '' });
    setMessage('');

    try {
      const res = await sendOtp(phone);
      if (res.success) {
        setMessage('認証コードを再送信しました。');
        startTimer();
      } else {
        setFormError('root', {
          type: 'manual',
          message: res.message || '認証コードの再送信に失敗しました',
        });
      }
    } catch (error) {
      setFormError('root', {
        type: 'manual',
        message: error.response?.data?.message || '通信エラーが発生しました',
      });
    }
  };

  return (
    <UserPageLayout title="OTP認証" maxWidth="sm">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 'none',
          boxShadow: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '15dvh',
          alignItems: 'flex-start',
        }}
      >
        <LabeledTextField
          label="認証コード"
          id="otp"
          name="otp"
          type="text"
          autoComplete="one-time-code"
          autoFocus
          placeholder="6桁の数字を入力してください"
          register={register}
          error={errors.otp}
          inputProps={{ maxLength: 6 }}
        />

        {errors.root && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.root.message}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        <Stack direction="column" spacing={2} sx={{ mt: 3, width: '100%' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ borderRadius: 5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? '確認中...' : '認証コード確認'}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            color="primary"
            onClick={resendOtp}
            disabled={!resendAvailable || isSubmitting}
            sx={{ borderRadius: 5 }}
          >
            {resendAvailable ? '認証コードを再送' : `再送まであと${timer}秒`}
          </Button>
        </Stack>
      </Box>
    </UserPageLayout>
  );
};

export default OtpVerificationPage;
